import { useMemo } from "react"

import type { RuleValues } from "~/utils/allocationRuleDrafts"
import { RULE_DEFAULTS, type RuleParams } from "~/utils/replenishmentRules"
import {
  ceilUnits,
  DEFAULT_OVERSTOCK_FACTOR,
  DEFAULT_TRANSIT_DAYS,
  outCoverDays,
  overstockFactorHint,
} from "~/utils/allocationRuleMaths"
import { rruleDayToWeekday, weekdayToRruleDay } from "~/utils/rruleWeekdays"
import { useReplenishmentRules, type ReplenishmentRuleWithMeta } from "../useAllocationData"

// Bridges the stored rule shape to the Rules editor's parameter set until the
// rule editor works on stored fields directly.

const RULE_WEEKDAY_CODES = new Set(["MO", "TU", "WE", "TH", "FR", "SA", "SU"])

export function toEditorRuleView(rule: ReplenishmentRuleWithMeta) {
  const factor = overstockFactorHint(rule, rule.fastestTransitDays)
  return {
    ...RULE_DEFAULTS,
    id: rule.id,
    name: rule.name,
    description: rule.description,
    dtstart: rule.dtstart,
    fastestTransitDays: rule.fastestTransitDays,
    thresholdMode: rule.unit === "quantity" ? ("units" as const) : ("days" as const),
    unitMin: rule.minStock,
    unitOpt: rule.upToStock,
    unitOver: rule.maxStock,
    safetyCoverDays: rule.safetyStockDays,
    cycleMode: rule.freq === "WEEKLY" ? ("schedule" as const) : ("fixed" as const),
    reviewCycleDays: rule.freq === "DAILY" ? rule.interval : RULE_DEFAULTS.reviewCycleDays,
    deliveryDays: rule.byDay.map(rruleDayToWeekday),
    repeatIntervalWeeks: rule.freq === "WEEKLY" ? rule.interval : 1,
    cutoffTime: rule.cutoff,
    overstockFactor: factor == null ? DEFAULT_OVERSTOCK_FACTOR : Math.round(factor * 100) / 100,
    updatedBy: rule.updatedBy,
    createdAt: rule.createdAt,
    updatedAt: rule.updatedAt,
    storeCount: rule.storeCount,
    audits: [] as {
      id: string
      ruleId: string
      createdBy: string
      action: string
      field: string
      before: string | null
      after: string | null
      createdAt: Date
    }[],
  }
}

export type EditorRuleView = ReturnType<typeof toEditorRuleView>

export function useRuleEditorRules() {
  const { rules, isLoading } = useReplenishmentRules()
  const editorRules = useMemo(() => rules.map(toEditorRuleView), [rules])
  return { rules: editorRules, isLoading }
}

/** The stored fields the editor's parameter set publishes as. */
export function editorParamsToRuleValues(
  params: RuleParams,
  meta: {
    name: string
    description: string | null
    dtstart?: string | null
    fastestTransitDays?: number
  },
): RuleValues {
  const byDay = params.deliveryDays
    .map(weekdayToRruleDay)
    .filter((day): day is RuleValues["byDay"][number] => RULE_WEEKDAY_CODES.has(day))
  const weekly = params.cycleMode === "schedule" && byDay.length > 0
  const schedule = weekly
    ? { freq: "WEEKLY" as const, byDay, interval: Math.min(12, params.repeatIntervalWeeks) }
    : {
        freq: "DAILY" as const,
        byDay: [],
        interval: Math.max(1, Math.round(params.reviewCycleDays)),
      }
  const safetyStockDays = Math.round(params.safetyCoverDays)
  const units = params.thresholdMode === "units"
  const transit = meta.fastestTransitDays ?? DEFAULT_TRANSIT_DAYS
  return {
    name: meta.name,
    description: meta.description,
    unit: units ? "quantity" : "day",
    minStock: params.unitMin,
    upToStock: params.unitOpt,
    maxStock: units
      ? params.unitOver
      : ceilUnits(params.overstockFactor * outCoverDays({ ...schedule, safetyStockDays }, transit)),
    safetyStockDays,
    ...schedule,
    dtstart: meta.dtstart ?? null,
    cutoff: params.cutoffTime,
  }
}
